import { onValue, ref, remove, set, update } from "firebase/database";
import React from "react";
import { MdDelete } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";
import { FaFilePdf, FaPlus } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { database } from "../../firebaseConfig";
import { shareEkkatharisiPdf } from "../../utils/ekkatharisiPdf";
import { eur, num, todayISO } from "../../utils/format";
import { setError, setLoading } from "../../redux/slices/ui";
import ModalAddFish from "./ModalAddFish";
import ModalComments from "./ModalComments";
import Question from "../ui/Question";
import PageHeader from "../ui/PageHeader";
import Button from "../ui/Button";
import { Input, Select } from "../ui/Field";

const sumFish = (fish) =>
  fish.quantities ? fish.quantities.reduce((sum, item) => sum + item.qty * item.price, 0) : 0;

const PromitheiaForm = () => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = React.useState(false);

  const { fDate, key } = useParams();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = React.useState([]);

  const [date, setDate] = React.useState(0);
  const [supplier, setSupplier] = React.useState(0);
  const [kivotia, setKivotia] = React.useState(0);
  const [promitheia, setPromitheia] = React.useState(0);
  const [somateio, setSomateio] = React.useState(0);
  const [diafora, setDiafora] = React.useState(0);
  const [etanal, setEtanal] = React.useState(2);
  const [metafora, setMetafora] = React.useState(0);
  const [trapeza, setTrapeza] = React.useState(0);
  const [comments, setComments] = React.useState("");
  const [fishes, setFishes] = React.useState([]);

  const [comm, setComm] = React.useState(0);
  const [emporevma, setEmporevma] = React.useState(0);
  const [ekkatharisi, setEkkatharisi] = React.useState(0);

  const [isReadOnly, setIsReadOnly] = React.useState(false);
  const [showDeleteQuestion, setShowDeleteQuestion] = React.useState(false);
  const [showComments, setShowComments] = React.useState(false);

  const [addFishData, setAddFishData] = React.useState({ name: "", kivotia: "", quantities: [], index: -1 });

  const openAddFishModal = (item = null, index = -1) => {
    if (item) {
      setAddFishData({ ...JSON.parse(JSON.stringify(item)), index });
    } else {
      setAddFishData({ name: "", kivotia: "", quantities: [], index });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setLoading(true));
    const data = {
      date,
      supplier,
      supplierName: suppliers.find((s) => s.id == supplier).name,
      kivotia: kivotia ?? 0,
      promitheia,
      somateio: somateio ?? 0,
      diafora: diafora ?? 0,
      etanal: etanal ?? 0,
      metafora: metafora ?? 0,
      trapeza: trapeza ?? 0,
      comments: comments ?? "",
      fishes,
      totalEmporevma: emporevma,
      totalPromitheia: comm,
      totalEkkatharisi: ekkatharisi,
      totalMetrita: ekkatharisi - (Number(trapeza) || 0),
    };
    try {
      if (key) {
        await update(ref(database, `ekkathariseis/${date}/${supplier}`), data);
      } else {
        await set(ref(database, `ekkathariseis/${date}/${supplier}`), data);
        setIsReadOnly(true);
        navigate(`/ekkathariseis`);
      }
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setLoading(false));
      dispatch(
        setError({ show: true, message: "Παρουσιάστηκε σφάλμα κατά την αποθήκευση της εκκαθάρισης." + error.message }),
      );
    }
  };

  // New εκκαθάριση → default the date to today (editable).
  React.useEffect(() => {
    if (!key) setDate(todayISO());
  }, []);

  React.useEffect(() => {
    const suppliersRef = ref(database, "suppliers");
    onValue(suppliersRef, (snapshot) => {
      const data = snapshot.val();
      const supplierList = data ? Object.entries(data).map(([k, value]) => ({ id: k, ...value })) : [];
      setSuppliers(supplierList);
    });
  }, []);

  const handleSupplier = (k) => {
    setSupplier(k);
    setPromitheia(suppliers.find((record) => record.id === k)?.commission);
  };

  React.useEffect(() => {
    const fishSum = fishes.reduce((total, record) => {
      if (record.quantities) {
        return total + record.quantities.reduce((fishTotal, fish) => fishTotal + fish.qty * fish.price, 0);
      }
      return 0;
    }, 0);

    setEmporevma(fishSum);
    setComm((fishSum * (promitheia ?? 0)) / 100);
    setEkkatharisi(
      fishSum -
        (fishSum * (promitheia ?? 0)) / 100 -
        (fishSum * (etanal ?? 0)) / 100 -
        (somateio ?? 0) -
        (metafora ?? 0) -
        (diafora ?? 0),
    );
  }, [fishes, promitheia, etanal, somateio, metafora, diafora]);

  React.useEffect(() => {
    if (key) {
      dispatch(setLoading(true));
      const reportRef = ref(database, `ekkathariseis/${fDate}/${key}`);
      onValue(
        reportRef,
        (snapshot) => {
          const data = snapshot.val();
          if (data) {
            setDate(data.date || "");
            setSupplier(data.supplier || "");
            setKivotia(data.kivotia || 0);
            setPromitheia(data.promitheia || 0);
            setSomateio(data.somateio || 0);
            setDiafora(data.diafora || 0);
            setEtanal(data.etanal || 0);
            setMetafora(data.metafora || 0);
            setTrapeza(data.trapeza || 0);
            setComments(data.comments || "");
            setFishes(data.fishes || []);
            setIsReadOnly(true);
          }
          dispatch(setLoading(false));
        },
        { onlyOnce: true },
      );
    }
  }, [key]);

  const handleConfirmDeleteQuestion = () => {
    if (key) {
      navigate("/ekkathariseis");
      remove(ref(database, `ekkathariseis/${fDate}/${key}`), {});
    }
    setShowDeleteQuestion(false);
  };

  const handleDownloadPdf = async () => {
    dispatch(setLoading(true));
    try {
      await shareEkkatharisiPdf({
        date,
        supplierName: suppliers.find((s) => s.id == supplier)?.name,
        promitheia,
        kivotia,
        somateio,
        etanal,
        metafora,
        diafora,
        trapeza,
        comments,
        fishes,
        emporevma,
        comm,
        ekkatharisi,
      });
    } catch (error) {
      dispatch(setError({ show: true, message: "Αποτυχία δημιουργίας PDF. " + (error?.message ?? "") }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const numberField = (label, value, onChange, disabled = false) => (
    <Input label={label} inputMode="decimal" value={value} disabled={disabled} onChange={onChange} />
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <PageHeader title={key ? "Επεξεργασία Εκκαθάρισης" : "Νέα Εκκαθάριση"}>
        {key && (
          <>
            <FaFilePdf className="cursor-pointer text-emerald-600" size={22} onClick={handleDownloadPdf} />
            <MdDelete className="cursor-pointer text-rose-500" size={24} onClick={() => setShowDeleteQuestion(true)} />
          </>
        )}
      </PageHeader>

      <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden px-4">
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          {/* Top fields */}
          <div className="mb-3 grid grid-cols-2 gap-2 rounded-2xl bg-white p-3 shadow-card">
            <Input floatAlways type="date" label="Ημ/νία" value={date} disabled={isReadOnly}
              onChange={(e) => setDate(e.target.value)} required />
            <Select label="Προμηθευτής" value={supplier} disabled={isReadOnly} required
              onChange={(e) => handleSupplier(e.target.value)}>
              <option value="">Επιλέξτε</option>
              {suppliers.map((sup) => (
                <option key={sup.id} value={sup.id}>
                  {sup.name}
                </option>
              ))}
            </Select>
            {numberField("Προμήθεια %", promitheia, (e) => setPromitheia(e.target.value.replace(",", ".")))}
            {numberField("Κιβώτια", kivotia, (e) => setKivotia(e.target.value.replace(",", ".")))}
            {numberField("Σωματείο", somateio, (e) => setSomateio(e.target.value.replace(",", ".")))}
            {numberField("ΕΤΑΝΑΛ %", etanal, (e) => setEtanal(e.target.value.replace(",", ".")))}
            {numberField("Μεταφ.", metafora, (e) => setMetafora(e.target.value.replace(",", ".")))}
            {numberField("Διάφορα", diafora, (e) => setDiafora(e.target.value.replace(",", ".")))}
          </div>

          {/* Fish */}
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-bold text-brand-700">Ψάρια</h3>
            <Button className="!px-3 !py-1.5 text-sm" onClick={() => openAddFishModal()}>
              <FaPlus size={12} /> Προσθήκη
            </Button>
          </div>

          <div className="space-y-2">
            {fishes.map((fish, index) => {
              const subtotal = sumFish(fish);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => openAddFishModal(fish, index)}
                  className="block w-full rounded-2xl border-l-4 border-brand-600 bg-white p-3 text-left shadow-card active:scale-[0.99]"
                >
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="truncate font-bold text-brand-700">{fish.name}</span>
                    <span className="flex shrink-0 items-center gap-2 text-sm text-slate-500">
                      {fish.kivotia > 0 ? `${fish.kivotia} Κιβ.` : ""}
                      <RiEdit2Fill className="text-brand-500" size={18} />
                    </span>
                  </div>
                  <table className="w-full text-right text-sm text-slate-600">
                    <thead>
                      <tr className="text-xs text-slate-400">
                        <th className="font-medium">Ποσ</th>
                        <th className="font-medium">Τιμή</th>
                        <th className="font-medium">Σύνολο</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fish.quantities?.map((q, i) => (
                        <tr key={i}>
                          <td>{num(q.qty)}</td>
                          <td>{eur(q.price)}</td>
                          <td>{eur(q.qty * q.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-1 border-t border-slate-200 pt-1 text-right">
                    <div className="font-bold text-slate-800">{eur(subtotal)}</div>
                    <div className="text-sm text-slate-500">
                      Προμήθεια: <span className="font-semibold">{eur((subtotal * promitheia) / 100)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bank amount — subtracted from the clearing to get cash */}
        <div className="mb-2 shrink-0">
          {numberField("Τράπεζα", trapeza, (e) => setTrapeza(e.target.value.replace(",", ".")))}
        </div>

        {/* Totals */}
        <div className="mb-2 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-800 px-3 py-2 text-white">
          <div className="grid grid-cols-3 gap-2 text-center leading-tight">
            <div>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Εμπόρευμα</div>
              <div className="text-sm font-bold">{eur(emporevma)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Προμήθεια</div>
              <div className="text-sm font-bold">{eur(comm)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Εκκαθάριση</div>
              <div className="text-base font-extrabold">{eur(ekkatharisi)}</div>
            </div>
          </div>
          <div className="mt-1.5 grid grid-cols-2 gap-2 border-t border-white/25 pt-1.5 text-center leading-tight">
            <div>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Τράπεζα</div>
              <div className="text-sm font-bold">-{eur(trapeza)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide opacity-80">Μετρητά</div>
              <div className="text-lg font-extrabold">{eur(ekkatharisi - (Number(trapeza) || 0))}</div>
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-brand-600/10 py-3">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Επιστροφή
          </Button>
          <Button variant="outline" onClick={() => setShowComments(true)}>
            Σχόλια
          </Button>
          <Button type="submit">Αποθήκευση</Button>
        </div>
      </form>

      <ModalAddFish
        show={showModal}
        handleClose={() => setShowModal(false)}
        data={addFishData}
        setData={setAddFishData}
        fishes={fishes}
        setFishes={setFishes}
      />
      <ModalComments show={showComments} setShow={setShowComments} comments={comments} setComments={setComments} />
      <Question
        show={showDeleteQuestion}
        onClose={() => setShowDeleteQuestion(false)}
        onConfirm={handleConfirmDeleteQuestion}
        message="Επιβεβαίωση διαγραφής"
      />
    </div>
  );
};

export default PromitheiaForm;
